<script setup lang="ts">
import { useSchool } from '~/composables/useSchool'
import { useRoute } from 'vue-router'

const isMobileMenuOpen = ref(false)
const { isDark, toggleTheme, initTheme } = useTheme()
const route = useRoute()

// Dynamic Logos
const logoUrl = ref('')
const logoAlt = ref('OXO Logo')

// Toggle Mobile Menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (isMobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

// Ensure menu closes on route change
watch(() => route.fullPath, () => {
  if (isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
    document.body.style.overflow = ''
  }
})

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Use AsyncData for SSR-compatible fetching
const { getSchoolBranding } = useSchool()
const { data: branding } = await useAsyncData('school-branding', () => getSchoolBranding())

// Sync values from branding data
watch(branding, (newVal) => {
  if (newVal) {
    if (newVal.logo) logoUrl.value = newVal.logo
    if (newVal.logoAlt) logoAlt.value = newVal.logoAlt
  }
}, { immediate: true })

onMounted(() => {
  // Init theme: Sync DOM with state
  initTheme()
})

onUnmounted(() => {
  // Cleanup scroll lock
  document.body.style.overflow = ''
})

const navLinks = [
  { label: "L'École", to: "/ecole" },
  { label: "Formations", to: "/formations" },
  { label: "Ateliers d'Été", to: "/ateliers-ete" },
  { label: "Projets", to: "/projets" },
  { label: "Actualités", to: "/actus" },
  { label: "FAQ", to: "/faq" },
]
</script>

<template>
  <div class="relative">
    <!-- Main Header Bar -->
    <header 
      class="fixed top-0 left-0 w-screen z-1010 transition-all duration-300 flex items-center bg-white/80 dark:bg-black/50 backdrop-blur-xl shadow-lg h-12 md:h-24"
    >
      <div class="w-full max-w-7xl mx-auto px-6 flex justify-between items-center relative z-60">
        
        <!-- Logo -->
        <NuxtLink to="/" class="flex items-center gap-2 group relative z-70" @click="scrollToTop">
          <NuxtImg v-if="logoUrl" :src="logoUrl" :alt="logoAlt" width="120" height="40" class="h-10 w-auto transition-transform duration-300 group-hover:scale-105" sizes="120px" />
        </NuxtLink>

        <!-- Desktop Nav -->
        <nav class="hidden lg:flex items-center font-sans text-sm font-bold tracking-wide uppercase">
          <ul class="flex items-center space-x-8 list-none p-0 m-0">
            <li v-for="link in navLinks" :key="link.to">
              <NuxtLink 
                :to="link.to" 
                active-class="text-oxo-red active-nav-item"
                class="hover:text-oxo-red transition relative group py-2 text-foreground font-bold block"
                @click="link.to === '/' ? scrollToTop() : null"
              >
                {{ link.label }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-oxo-red transition-all duration-300 group-hover:w-full"></span>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <!-- Actions Desktop -->
        <div class="hidden lg:flex items-center gap-6">
          <AppButton to="/contact" variant="secondary" size="sm">
            Contact
          </AppButton>
          <AppButton to="/candidature" variant="primary" size="sm">
            Candidater <i class="fas fa-chevron-right text-xs ml-1" aria-hidden="true"></i>
          </AppButton>
        </div>

        <!-- Mobile Menu Toggle -->
        <button 
          @click="toggleMobileMenu" 
          class="lg:hidden text-foreground text-2xl z-70 relative focus:outline-hidden w-10 h-10 flex items-center justify-center transition-transform active:scale-90"
          aria-label="Menu"
        >
          <i :class="['fas', isMobileMenuOpen ? 'fa-times' : 'fa-bars']" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <!-- Mobile Fullscreen Menu (Teleported to body level to avoid any clipping/stacking issues) -->
    <Teleport to="body">
      <Transition name="mobile-menu">
        <div 
          v-if="isMobileMenuOpen" 
          class="fixed inset-0 bg-background/98 backdrop-blur-2xl z-1000 lg:hidden flex flex-col pt-32 pb-12 overflow-y-auto overflow-x-hidden min-h-dvh"
        >
          <!-- Decorative Background Elements -->
          <div class="absolute inset-0 pointer-events-none z-0 opacity-20">
            <div class="absolute -top-20 -left-20 w-80 h-80 bg-oxo-red/20 rounded-full blur-[100px] animate-pulse"></div>
            <div class="absolute -bottom-20 -right-20 w-80 h-80 bg-oxo-yellow/20 rounded-full blur-[100px] animate-pulse delay-1000"></div>
          </div>

          <div class="relative z-10 w-full max-w-sm mx-auto px-8">
            <nav class="mb-12">
              <ul class="flex flex-col gap-6 font-display text-2xl font-black uppercase tracking-wider list-none p-0 m-0">
                <li v-for="(link, index) in navLinks" :key="link.to">
                  <NuxtLink 
                    :to="link.to" 
                    active-class="text-oxo-red"
                    class="group flex items-center gap-6 text-foreground hover:text-oxo-red transition-all duration-300"
                    @click="toggleMobileMenu"
                  >
                    <span class="relative">
                      {{ link.label }}
                      <span class="absolute -bottom-1 left-0 w-0 h-[2px] bg-oxo-red transition-all duration-300 group-hover:w-full"></span>
                    </span>
                  </NuxtLink>
                </li>
              </ul>
            </nav>
            
            <!-- Separator Line -->
            <div class="w-16 h-1 bg-linear-to-r from-oxo-red to-oxo-yellow rounded-full mb-12"></div>
            
            <!-- CTAs Mobile -->
            <div class="flex flex-col gap-4">
              <AppButton to="/contact" variant="secondary" size="lg" @click="toggleMobileMenu" class="w-full justify-center">
                Contact
              </AppButton>
              <AppButton to="/candidature" variant="primary" size="lg" @click="toggleMobileMenu" class="w-full justify-center">
                Candidater
              </AppButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Stacking Context Logic */
.active-nav-item span {
  width: 100%;
}

/* Mobile Menu Transitions */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
